import {
  Box,
  Button,
  ClickAwayListener,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from "@mui/material";
import { ContactViewContext } from "contexts/ContactView";
import { TrackingColumnsContext } from "contexts/TrackingColumns";
import { useContext, useRef, useState } from "react";
import { AutomatedContactProperty, OutreachContact } from "schemas/dashboard";

import { SelectOption } from "features/Influencer/Tracking/schema";

import { buttonStyles as styles } from "./styles";

export default function MoveBrandToButton({
  outreachContact,
}: {
  outreachContact: OutreachContact;
}) {
  const {
    selectedView,
    setOutreachContacts,
    updateOutreachContactPropertyValues,
    trackTable,
    setTotal,
  } = useContext(ContactViewContext);
  const { contactProperties } = useContext(TrackingColumnsContext);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);

  const handleToggle = () => {
    trackTable("Tracking Table Action Pressed", {
      action: "Move To Action Button",
      "Outreach Contact ID": outreachContact?.id,
      "Outreach Contact Email": outreachContact?.email,
    });
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  const stageProperty = contactProperties.find(
    (p) => p.automatedProperty === AutomatedContactProperty.stage,
  );

  if (!stageProperty || !stageProperty.id) {
    return <></>;
  }

  const options = stageProperty?.selectOptions?.filter(
    (o) =>
      stageProperty.id &&
      (o.id !== outreachContact.propertyValues?.[stageProperty.id] ||
        selectedView?.name === "Imports"),
  );

  const changeStage = (option: SelectOption) => {
    trackTable("Move To Action Changed Stage", {
      newStage: option.name,
      "Outreach Contact ID": outreachContact?.id,
      "Outreach Contact Email": outreachContact?.email,
    });

    setOutreachContacts((prev) => {
      let pValues;
      const copy = prev.map((contact) => {
        if (contact.id !== outreachContact.id) {
          return contact;
        }
        pValues = { ...contact.propertyValues };
        if (stageProperty.id) {
          pValues[stageProperty.id] = option.id;
        }

        return {
          ...contact,
          propertyValues: pValues,
        };
      });
      if (pValues && stageProperty.id && outreachContact.id) {
        updateOutreachContactPropertyValues(
          outreachContact.id,
          stageProperty.id,
          pValues[stageProperty.id],
        );
      }
      if (selectedView?.name === "All") {
        return copy;
      } else {
        setTotal((prev) => prev - 1);
        return copy.filter((c) => c.id !== outreachContact.id);
      }
    });
  };

  return (
    <Box display="flex" justifyContent="center" sx={{ minWidth: "160px" }}>
      <Button
        ref={anchorRef}
        tabIndex={-1}
        size="small"
        variant="text"
        color="primary"
        sx={styles.smallButton}
        id="move-brand-to-button"
        aria-controls={open ? "composition-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        Move Brand To
      </Button>
      <Popper
        open={open}
        ref={popperRef}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: 2 }}
      >
        <Paper sx={{ fontSize: 12, p: 1, background: "white" }} elevation={2}>
          <ClickAwayListener onClickAway={handleClose}>
            <MenuList
              id="move-brand-to-menu"
              aria-labelledby="move-brand-to-button"
            >
              {options?.map((option) => (
                <MenuItem key={option.id} onClick={() => changeStage(option)}>
                  {option.name}
                </MenuItem>
              ))}
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>
    </Box>
  );
}
